import { useEffect, useRef, useState } from 'react';
import { createUuid } from '@notifi/uuid/browser';
import { toast } from 'sonner';
import { apiGet, apiPost, operationsClient } from '@/lib/api';
import { normalizeError } from '@/lib/api/errors';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/atoms/dialog';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';

export interface ScheduleNotificationChoice {
  sms?: boolean;
  email?: boolean;
  requestId?: string;
  includeInitialConfirmation?: boolean;
}
export interface ScheduleNotificationOptions {
  ask: boolean;
  phone: string | null;
  email: string | null;
}
export async function getScheduleNotificationOptions(
  id: string,
  includeInitialConfirmation = false,
): Promise<ScheduleNotificationOptions> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await apiGet<ScheduleNotificationOptions>(
        operationsClient,
        `/appointments/${id}/schedule-change-notification${includeInitialConfirmation ? '?includeInitialConfirmation=true' : ''}`,
      );
    } catch (error) {
      const normalized = normalizeError(error);
      // Scheduling permission does not require permission to send reminders.
      if (normalized.status === 403) return { ask: false, phone: null, email: null };
      // Retry this read during a brief network outage or local server reload.
      if (attempt >= 2 || (normalized.status > 0 && normalized.status < 500)) throw error;
      await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
    }
  }
}

export function ScheduleNotificationFields({
  options,
  value,
  onChange,
  initialConfirmation = false,
}: {
  options: ScheduleNotificationOptions | null;
  value: ScheduleNotificationChoice;
  onChange: (value: ScheduleNotificationChoice) => void;
  initialConfirmation?: boolean;
}) {
  if (!options?.ask) return null;
  return (
    <div className="space-y-3 rounded-lg border p-3">
      <p className="text-sm">
        {initialConfirmation
          ? 'Send the updated visit date and arrival window for the customer to confirm.'
          : 'This customer received a confirmation for the previous schedule. Send the updated visit date and arrival window for them to confirm again?'}
      </p>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={Boolean(value.email)}
          disabled={!options.email}
          onCheckedChange={(checked) => onChange({ ...value, email: checked === true })}
        />
        Send email{!options.email && <span className="text-muted-foreground">(unavailable)</span>}
      </label>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={Boolean(value.sms)}
          disabled={!options.phone}
          onCheckedChange={(checked) => onChange({ ...value, sms: checked === true })}
        />
        Send SMS{!options.phone && <span className="text-muted-foreground">(unavailable)</span>}
      </label>
    </div>
  );
}

export async function sendScheduleChangeNotification(
  id: string,
  choice: ScheduleNotificationChoice | undefined,
  window: { start: string; end: string } | null | undefined,
) {
  if (!choice?.sms && !choice?.email) return;
  const requestId = choice.requestId ?? createUuid();
  const send = async () => {
    if (!window) throw new Error('The new arrival window could not be loaded.');
    await apiPost(operationsClient, `/appointments/${id}/schedule-change-notification`, {
      channels: choice.sms && choice.email ? 'BOTH' : choice.sms ? 'SMS' : 'EMAIL',
      requestId,
      expectedStartAt: window.start,
      expectedEndAt: window.end,
      ...(choice.includeInitialConfirmation ? { includeInitialConfirmation: true } : {}),
    });
    toast.success('New schedule confirmation queued.');
  };
  try {
    await send();
  } catch {
    toast.error('Schedule saved, but the new confirmation could not be queued.', {
      duration: 15000,
      action: {
        label: 'Retry',
        onClick: () => {
          void send().catch(() =>
            toast.error('Confirmation could not be queued. Open the visit to try again.'),
          );
        },
      },
    });
  }
}

export function useScheduleChangeNotification() {
  const [options, setOptions] = useState<ScheduleNotificationOptions | null>(null);
  const [choice, setChoice] = useState<ScheduleNotificationChoice>({});
  const resolve = useRef<((value: ScheduleNotificationChoice | null) => void) | null>(null);
  useEffect(() => () => resolve.current?.(null), []);
  const settle = (value: ScheduleNotificationChoice | null) => {
    setOptions(null);
    resolve.current?.(value);
    resolve.current = null;
  };
  const ask = async (id: string): Promise<ScheduleNotificationChoice | null> => {
    let loaded: ScheduleNotificationOptions;
    try {
      loaded = await getScheduleNotificationOptions(id);
    } catch {
      toast.error('Could not check notification options. Please try saving again.');
      return null;
    }
    if (!loaded.ask) return {};
    setChoice({});
    setOptions(loaded);
    return new Promise((done) => {
      resolve.current = done;
    });
  };
  return {
    ask,
    dialog: (
      <Dialog
        open={Boolean(options)}
        onOpenChange={(open) => {
          if (!open) settle(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Visit date or arrival window changed</DialogTitle>
            <DialogDescription>
              Save the updated visit date and arrival window, and optionally notify the customer.
              Their previous schedule will be replaced.
            </DialogDescription>
          </DialogHeader>
          <ScheduleNotificationFields options={options} value={choice} onChange={setChoice} />
          <DialogFooter>
            <Button variant="outline" onClick={() => settle(null)}>
              Cancel changes
            </Button>
            <Button onClick={() => settle({ ...choice, requestId: createUuid() })}>
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    ),
  };
}
